import type { TaskJobData } from "@/types/jobs";

export interface SubTaskState {
  subTaskId: string;
  parentTaskId: string;
  failed: number;
  total: number;
  taskType: string;
  complete: number;
  running: number;
}

export const useInitialBulkTaskState = createSharedComposable(() => {
  const { data: tasks, isFetching, refetch } = useListTasks(true);

  const parentTasks = ref<string[]>([]);
  const events = reactive<Record<string, SubTaskState>>({});

  const taskState = computed(() => {
    const initialTasks = tasks.value?.filter(
      task => task.taskType === "content/combined/write-posts"
    );
    const total = initialTasks?.length || 0;
    const hasOtherTasks = tasks.value && tasks.value.length > total;
    const failed = initialTasks?.filter(task => task.error)?.length || 0;
    const processingTasks = initialTasks?.filter(task => task.running || task.queued);

    return {
      total,
      initialTasks,
      processingCount: processingTasks?.length || 0,
      done: total - (processingTasks?.length || 0),
      failed,
      initialTaskMap,
      hasOtherTasks,
    };
  });

  const isLoading = computed(
    () => isFetching.value && !taskState.value.initialTasks?.length
  );

  const initialTaskMap = computed(() => {
    const initialTaskMap: Record<string, boolean> = {};

    parentTasks.value.forEach(taskId => {
      initialTaskMap[taskId] = true;
    });
    taskState.value.initialTasks?.forEach(task => {
      initialTaskMap[task.id] = true;
    });
    return initialTaskMap;
  });

  function maybeAddParentTaskEvent(data: TaskJobData) {
    if (!data || !data.taskId) return;
    const { taskId, taskType } = data;
    if (taskType !== "content/combined/write-posts") return;
    const oldLength = parentTasks.value.length;
    parentTasks.value = Array.from(new Set([...parentTasks.value, taskId]));
    const newLength = parentTasks.value.length;
    if (oldLength !== newLength) refetch();
  }

  function maybeAddSubtaskEvent(data: TaskJobData) {
    if (!data || !data.subTaskId) return;
    const { currentStatus, subTaskId, taskId, taskType } = data;
    if (!initialTaskMap.value[taskId]) return;

    const existing = events[taskType];

    const failed =
      currentStatus === "error" ? (existing?.failed || 0) + 1 : existing?.failed || 0;
    const complete =
      currentStatus === "complete"
        ? (existing?.complete || 0) + 1
        : existing?.complete || 0;
    let running =
      currentStatus === "running"
        ? (existing?.running || 0) + 1
        : (existing?.running || 0) - 1;

    running = Math.max(running, 0);

    const total = failed + complete + running;

    events[taskType] = {
      subTaskId,
      parentTaskId: taskId,
      failed,
      total,
      taskType,
      complete,
      running,
    };
  }

  return {
    tasks,
    isFetching,
    taskState,
    events,
    isLoading,
    initialTaskMap,
    parentTasks,
    maybeAddSubtaskEvent,
    maybeAddParentTaskEvent,
  };
});
